import { FC, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Typography } from "antd";
import { FallbackProps } from "react-error-boundary";
import styles from "./styles.module.scss";
import { LeftOutline } from "antd-mobile-icons";
import { useAppNavigate } from "../../../hooks/router";
import { analyticsController, authController } from "../../../AppController";
import { logger } from "../../../util/logger";
import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../hooks/redux";
import { setAuthState } from "../../../redux/Auth/auth.slice";
import { signOutFirebase } from "../../../firebase/auth";

const { Title, Text } = Typography;

interface ErrorComponentProps extends FallbackProps {
  connectionStatus?: boolean; // kept for backwards-compat; ignored (read from Redux)
}

/**
 * Buckets of error the route-level boundary can recover from. The classifier
 * below maps `error.name` + `error.message` to one of these so the JSX branch
 * can show a message and an action that's actually useful (instead of a flat
 * "Something Went Wrong" for everything).
 *
 * NOTE: we only see a raw `Error` here — HTTP status doesn't survive the
 * boundary unless the thrower attaches it. For API-level classification by
 * status code, see the per-hook handlers (e.g. Step1Account/useHook.ts).
 */
type ErrorBucket = "chunkLoad" | "auth" | "server" | "network" | "unknown";

const AUTH_PATTERNS = /\b401\b|\b403\b|unauthori[sz]ed|forbidden/i;
const SERVER_PATTERNS =
  /\b5\d{2}\b|internal server error|bad gateway|service unavailable|gateway timeout/i;
const NETWORK_PATTERNS =
  /failed to fetch|network ?error|networkrequestfailed|load failed/i;
// Chunk-load failure wording varies by bundler + browser. We collect every
// variant we've seen in the wild so a stale/offline chunk fetch lands in the
// `chunkLoad` bucket (Reload CTA) instead of falling through to the generic
// "Something went wrong" screen.
//
//   Webpack         → "ChunkLoadError", "Loading chunk 42 failed."
//   Vite (Chrome)   → "Failed to fetch dynamically imported module: …"
//   Vite (Safari)   → "Importing a module script failed.",
//                     "Error loading dynamically imported module: …"
//   Vite CSS helper → "Unable to preload CSS for /assets/Foo-abc123.css"
const CHUNK_PATTERNS =
  /chunkloaderror|loading chunk \d+ failed|failed to fetch dynamically imported|dynamically imported module|importing a module script failed|unable to preload css|failed to load module script/i;

function classifyError(error: Error | null | undefined): ErrorBucket {
  if (!error) return "unknown";
  const haystack = `${error.name} ${error.message}`;
  if (CHUNK_PATTERNS.test(haystack)) return "chunkLoad";
  if (AUTH_PATTERNS.test(haystack)) return "auth";
  if (SERVER_PATTERNS.test(haystack)) return "server";
  if (error.name === "AbortError" || NETWORK_PATTERNS.test(haystack)) {
    return "network";
  }
  return "unknown";
}

export const ErrorComponent: FC<ErrorComponentProps> = ({
  error,
  resetErrorBoundary,
}) => {
  const { t } = useTranslation();
  const navigate = useAppNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const { user, userClinic, connectionStatus } = useAppSelector((state) => ({
    user: state.auth.user,
    userClinic: state.clinic.userClinic,
    connectionStatus: state.network.connectionStatus,
  }));

  const bucket = useMemo<ErrorBucket>(() => classifyError(error), [error]);

  const logErrorBoundary = (err: Error) => {
    const errorArr = err.stack?.split("\n");

    analyticsController.logEvent("error-boundry-exception", {
      email: user?.email,
      selectedFacility: userClinic?.name,
      selectedFacilityId: userClinic?.id,
      cUserId: user?.userID,
      location: location,
      error: {
        name: err.name,
        stack: errorArr,
        cause: err.cause,
        message: err.message,
      },
    });

    // Structured log so render crashes are queryable in Sentry Logs alongside
    // API/app errors (and carry the device/lifecycle breadcrumb trail).
    logger.error("react_error_boundary", {
      error_name: err.name,
      error_message: err.message,
      bucket,
      facilityId: userClinic?.id,
      location: typeof location === "string" ? location : location?.pathname,
    });
  };

  useEffect(() => {
    // Auto-recover stale chunk loads when we're back online — the next
    // re-render will fetch the new bundle. Uses the same CHUNK_PATTERNS
    // regex as `classifyError` so Webpack, Vite-on-Chrome, Vite-on-Safari,
    // and Vite-CSS-preload failures all benefit from the same recovery
    // (previously this only matched Chrome's "dynamically imported module"
    // wording and left Safari/CSS-preload errors stuck on the error screen).
    if (
      connectionStatus &&
      error &&
      CHUNK_PATTERNS.test(`${error.name} ${error.message}`)
    ) {
      resetErrorBoundary();
      return;
    }

    if (error) {
      logErrorBoundary(error);
    }
  }, [connectionStatus, error, resetErrorBoundary]);

  // Sign the user out for the auth bucket. We deliberately don't pull in
  // useAuth() because it requires a fistful of providers that aren't
  // available at every mount point of this component (Public layout, etc).
  // Instead we call the singleton auth/firebase helpers directly so the
  // Cognito + Firebase sessions are actually invalidated — otherwise
  // reloading after this "logout" would silently re-authenticate the user
  // with the same expired session that tripped the error boundary.
  const handleLogOut = async () => {
    try {
      await signOutFirebase();
    } catch {
      /* best-effort — keep going so Cognito + Redux still get cleared */
    }
    try {
      await authController.logoutUser();
    } catch {
      /* best-effort — Redux clear + redirect still need to happen */
    }
    dispatch(
      setAuthState({
        user: undefined,
        isAuthorized: false,
        isPinAuthorized: false,
      })
    );
    window.location.hash = "/login";
  };

  const handleReload = () => {
    window.location.reload();
  };

  // Route-aware "return to dashboard" target. If the crash happened on a
  // setup sub-route (e.g. /setup/account/:id, /setup/users/:id), bail out
  // to the technician's setup dashboard at /setup. Anywhere else — including
  // /setup itself, where navigating to /setup would just reload the broken
  // page — exit to the main app at /. Falls back to navigate(-1) on the
  // off chance pathname is empty (defensive; shouldn't happen in practice).
  const inSetupSubRoute =
    location.pathname.startsWith("/setup/") && location.pathname !== "/setup";
  const dashboardLabelKey = inSetupSubRoute
    ? "errorComponent.backToSetupDashboard"
    : "errorComponent.backToDashboard";
  const goToDashboard = () => {
    if (!location.pathname) {
      navigate(-1);
      return;
    }
    navigate(inSetupSubRoute ? "/setup" : "/");
  };

  // Offline check is the highest-priority branch — the user has no way
  // to recover until the network returns.
  if (!connectionStatus) {
    return (
      <div className={styles.noWorryContainer}>
        <Title level={4} className={styles.heading}>
          {t("errorComponent.noInternet")}
        </Title>
        <Text className={styles.subText}>
          {t("errorComponent.noInternetDesc")}
        </Text>
      </div>
    );
  }

  // Chunk-load auto-recover is handled in the effect; if we still get here
  // with that error, fall through to the chunkLoad branch's Reload action.
  if (!error) return null;

  let title: string;
  let body: string;
  let primaryLabel: string;
  let onPrimary: () => void;
  let secondaryLabel: string | null = null;
  let onSecondary: (() => void) | null = null;

  switch (bucket) {
    case "chunkLoad":
      // Chunk-load failures have two real-world causes that we can't tell
      // apart from the error message alone:
      //   1. The user's network blipped while the JS/CSS file was downloading
      //      (overwhelmingly the common case for field-setup technicians on
      //      spotty connections).
      //   2. The bundle hash changed under them after a deploy (rarer; only
      //      happens in the brief window before CloudFront invalidation +
      //      browser cache catch up).
      // We surface the network-flavoured copy because it's honest for case 1
      // and still actionable for case 2 — and we keep the Reload action
      // because a full page reload fixes both (re-fetches index.html, picks
      // up the new bundle hashes, and retries the failed download once the
      // network is back). "Try again" / resetErrorBoundary would loop on the
      // same stale hash for case 2.
      title = t("errorComponent.networkTitle");
      body = t("errorComponent.networkBody");
      primaryLabel = t("errorComponent.reload");
      onPrimary = handleReload;
      break;

    case "auth":
      title = t("errorComponent.authTitle");
      body = t("errorComponent.authBody");
      primaryLabel = t("errorComponent.logOut");
      onPrimary = handleLogOut;
      break;

    case "server":
      title = t("errorComponent.serverTitle");
      body = t("errorComponent.serverBody");
      primaryLabel = t("errorComponent.tryAgain");
      onPrimary = resetErrorBoundary;
      secondaryLabel = t("errorComponent.contactSupport");
      onSecondary = () => {
        window.location.href = "mailto:support@circadiahealth.com";
      };
      break;

    case "network":
      title = t("errorComponent.networkTitle");
      body = t("errorComponent.networkBody");
      primaryLabel = t("errorComponent.tryAgain");
      onPrimary = resetErrorBoundary;
      break;

    case "unknown":
    default:
      title = t("errorComponent.somethingWrong");
      body = t("errorComponent.unknownBody");
      primaryLabel = t("errorComponent.tryAgain");
      onPrimary = resetErrorBoundary;
      secondaryLabel = t(dashboardLabelKey);
      onSecondary = goToDashboard;
      break;
  }

  return (
    <>
      <span id="back-icon" className={styles.backButton}>
        <LeftOutline className={styles.backIcon} onClick={() => navigate(-1)} />
      </span>
      <div className={styles.noWorryContainer}>
        <Title level={4} className={styles.heading}>
          {title}
        </Title>
        <Text className={styles.subText}>{body}</Text>
        <div className={styles.actionRow}>
          <button
            type="button"
            className={styles.primaryAction}
            onClick={onPrimary}
          >
            {primaryLabel}
          </button>
          {secondaryLabel && onSecondary && (
            <button
              type="button"
              className={styles.secondaryAction}
              onClick={onSecondary}
            >
              {secondaryLabel}
            </button>
          )}
        </div>
      </div>
    </>
  );
};
